import copy from './copy.json'

const MS_PER_DAY = 1000 * 60 * 60 * 24

/**
 * What the days-since-last-tag column shows for a camper who has tags but whose
 * most recent tag has no recorded time.
 *
 * AC-10 requires this to be non-blank and visibly distinct from what a
 * never-tagged camper shows, because blank already means "never tagged" and a
 * camper with twelve tags is not that.
 */
export const UNKNOWN_DAYS_LABEL = 'Unknown'

/** Which fact a days-since-last-tag figure was computed from (AC-25). */
export type DaysSource = 'tag' | 'photo' | 'none'

export interface DaysSinceLastTag {
  /** '' for a never-tagged camper, UNKNOWN_DAYS_LABEL when neither fact exists, else a bare count. */
  days: string
  /** Which fact `days` came from; 'none' whenever `days` carries no figure. */
  source: DaysSource
}

/** What the *Based On* column reads for each source (AC-25). */
export const daysSourceLabel = (source: DaysSource): string => {
  if (source === 'tag') return copy.report.daysSource.tag
  if (source === 'photo') return copy.report.daysSource.photo
  return ''
}

const wholeDaysSince = (timestamp: string, now: Date): string | null => {
  const parsed = new Date(timestamp)
  // A non-null but unparseable value makes an Invalid Date, whose arithmetic is
  // NaN — which would print as "NaN" in a column a human reads as a number.
  if (Number.isNaN(parsed.getTime())) return null

  const elapsed = now.getTime() - parsed.getTime()
  // Clamp: a clock skew between the browser and the service must not print a
  // negative number of days.
  return String(Math.max(0, Math.floor(elapsed / MS_PER_DAY)))
}

/**
 * The four states of the days-since-last-tag column (E-8):
 *   tags + a recorded tag time                 -> whole days since it, source 'tag'
 *   tags + no tag time but a photo date        -> whole days since the photo, source 'photo'
 *   tags + neither fact                        -> UNKNOWN_DAYS_LABEL, source 'none'
 *   no tags                                    -> blank, source 'none'
 *
 * One function returns both the figure and the fact it came from (D-Q), so the
 * number and its label cannot disagree — the guarantee AC-26 needs. A tag time,
 * where one exists, is always preferred: the fallback must never quietly take
 * over from real tag data.
 *
 * `lastTaggedPhotoCreatedUTC` is treated as an already-correct UTC instant and
 * parsed exactly as `lastTaggedUTC` is — no further timezone adjustment.
 *
 * `now` is injected so the column is testable without freezing the clock.
 */
export const formatDaysSinceLastTag = (
  taggedPhotoCount: number,
  lastTaggedUTC: string | null,
  lastTaggedPhotoCreatedUTC: string | null,
  now: Date = new Date(),
): DaysSinceLastTag => {
  if (!taggedPhotoCount || taggedPhotoCount <= 0)
    return {days: '', source: 'none'}

  const fromTag = lastTaggedUTC ? wholeDaysSince(lastTaggedUTC, now) : null
  if (fromTag !== null) return {days: fromTag, source: 'tag'}

  // AC-24 — the tag carried no usable time, so the photo's own creation date is
  // the best available answer. At two of four camps sampled on QA03 this is the
  // majority of rows, which is why it is labelled rather than footnoted (D-O).
  const fromPhoto = lastTaggedPhotoCreatedUTC
    ? wholeDaysSince(lastTaggedPhotoCreatedUTC, now)
    : null
  if (fromPhoto !== null) return {days: fromPhoto, source: 'photo'}

  // AC-28's floor: both facts absent, so the old Unknown survives — with no
  // source, because no figure was produced.
  return {days: UNKNOWN_DAYS_LABEL, source: 'none'}
}

/** Training-photo status as an explicit answer — never a blank cell. */
export const formatTrained = (trained: boolean): string =>
  trained ? 'Yes' : 'No'

/**
 * Resolve a set of ids to their display names.
 *
 * An id with no resolved name is dropped rather than printed as a bare number:
 * an internal primary key in a column of names reads as data corruption to the
 * camp reading the page.
 */
export const joinNames = (ids: number[], names: Map<number, string>): string =>
  ids
    .map((id) => names.get(id))
    .filter((name): name is string => Boolean(name))
    .join(', ')

/** AC-7 — surname, then given name. Case-insensitive so casing can't reorder. */
export const compareByName = (
  a: {lastName: string; firstName: string},
  b: {lastName: string; firstName: string},
): number => {
  const byLast = a.lastName.localeCompare(b.lastName, undefined, {
    sensitivity: 'base',
  })
  if (byLast !== 0) return byLast
  return a.firstName.localeCompare(b.firstName, undefined, {
    sensitivity: 'base',
  })
}
